/**
 * Feature flags Rintintin Booking — alignés sur PostgreSQL ``FEATURE_FLAG``
 * (API publique ``GET /feature-flags``, admin Rantanplan).
 *
 * Contrat zéro-régression : absent / true = comportement historique ON.
 * Seul ``enabled: false`` coupe. Default-off uniquement pour flags bêta listés.
 */
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { buildApiUrl } from '../config/apiConfig';

export enum FeatureFlag {
  /** Kill-switch global app booking (maintenance client). */
  BOOKING_ACCESS = 'BOOKING_ACCESS',
  /** Parcours réservation service. */
  BOOKING_FLOW = 'BOOKING_FLOW',
  /** Confirm / decline token. */
  BOOKING_TOKEN_ACTIONS = 'BOOKING_TOKEN_ACTIONS',
  /** Fiche pro moderne. */
  PRO_PAGE = 'PRO_PAGE',
  /** Page service dédiée. */
  SERVICE_PAGE = 'SERVICE_PAGE',
  /** Pension — partagé avec backoffice. */
  BOARDING_MODULE = 'BOARDING_MODULE',
  /** Forfaits. */
  FORFAITS = 'FORFAITS',
  /** Packs crédits. */
  CREDITS = 'CREDITS',
  /** Abonnements club SEPA. */
  CLUB_SUBSCRIPTIONS = 'CLUB_SUBSCRIPTIONS',
  /** Messagerie client Twilio — partagé (CHAT_FEATURE). */
  CHAT_FEATURE = 'CHAT_FEATURE',
  /** Documents / CGV token. */
  DOCUMENTS_CGV = 'DOCUMENTS_CGV',
  /** Devis client. */
  QUOTES = 'QUOTES',
  /** Checkout / panier. */
  CHECKOUT_PAYMENTS = 'CHECKOUT_PAYMENTS',
  /** Widget embed — partagé. */
  BOOKING_WIDGET = 'BOOKING_WIDGET',
  /** Discovery /recherche. */
  PRO_DISCOVERY = 'PRO_DISCOVERY',
  /** Mode question (phrase → teaser + pros). Kill-switch ; absent/true = ON. */
  BOOKING_ASK = 'BOOKING_ASK',
  /** Événements. */
  EVENTS = 'EVENTS',
  /** Favoris. */
  FAVORITES = 'FAVORITES',
  /** Comptes-rendus. */
  CLIENT_REPORTS = 'CLIENT_REPORTS',
  /** Carnet de santé animal (bêta). */
  ANIMAL_HEALTH = 'ANIMAL_HEALTH',
  /** Page association publique. */
  ASSOCIATION_PAGE = 'ASSOCIATION_PAGE',
  /**
   * Fiches de pros non inscrits (annuaire booking).
   * Off si absent : la recherche et la carte restent celles des pros inscrits.
   */
  BOOKING_DIRECTORY_LISTINGS = 'BOOKING_DIRECTORY_LISTINGS',
}

/** Bêta / déploiement progressif — off si absent. */
const DEFAULT_OFF_FLAGS: ReadonlySet<FeatureFlag> = new Set<FeatureFlag>([
  FeatureFlag.ANIMAL_HEALTH,
  FeatureFlag.BOOKING_DIRECTORY_LISTINGS,
]);

type LoadStatus = 'idle' | 'loaded' | 'error';

export function resolveFeatureFlagEnabled(
  featureName: FeatureFlag,
  remoteFlags: Record<string, boolean>,
  status: LoadStatus,
): boolean {
  const isDefaultOff = DEFAULT_OFF_FLAGS.has(featureName);
  if (status !== 'loaded') {
    return !isDefaultOff;
  }
  const v = remoteFlags[featureName];
  if (v === undefined) {
    return !isDefaultOff;
  }
  return v !== false;
}

interface FeatureFlagsResponse {
  flags?: Record<string, boolean>;
}

interface FeatureFlagsContextType {
  isFeatureEnabled: (featureName: FeatureFlag) => boolean;
  status: LoadStatus;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

const FEATURE_FLAGS_CACHE_KEY = 'rtt-booking-feature-flags-cache';
const FEATURE_FLAGS_CACHE_TTL_MS = 5 * 60 * 1000;

function readFeatureFlagsCache(): Record<string, boolean> | null {
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { flags?: Record<string, boolean>; ts?: number };
    if (!parsed.ts || Date.now() - parsed.ts >= FEATURE_FLAGS_CACHE_TTL_MS) {
      return null;
    }
    return parsed.flags ?? {};
  } catch {
    return null;
  }
}

function writeFeatureFlagsCache(flags: Record<string, boolean>) {
  try {
    localStorage.setItem(
      FEATURE_FLAGS_CACHE_KEY,
      JSON.stringify({ flags, ts: Date.now() }),
    );
  } catch {
    /* private mode */
  }
}

export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const cachedFlags = readFeatureFlagsCache();
  const [remoteFlags, setRemoteFlags] = useState<Record<string, boolean>>(cachedFlags ?? {});
  const [status, setStatus] = useState<LoadStatus>(cachedFlags ? 'loaded' : 'idle');

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(buildApiUrl('/feature-flags'), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as FeatureFlagsResponse;
        if (!cancelled) {
          const flags = data.flags ?? {};
          setRemoteFlags(flags);
          setStatus('loaded');
          writeFeatureFlagsCache(flags);
        }
      } catch {
        if (!cancelled && !cachedFlags) {
          setRemoteFlags({});
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Mount-only remote fetch; cachedFlags lu au démarrage volontairement.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-fetch on cache identity churn
  }, []);

  const isFeatureEnabled = useCallback(
    (featureName: FeatureFlag): boolean =>
      resolveFeatureFlagEnabled(featureName, remoteFlags, status),
    [status, remoteFlags],
  );

  return (
    <FeatureFlagsContext.Provider value={{ isFeatureEnabled, status }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlagsOptional = () => useContext(FeatureFlagsContext);

export const useFeatureFlags = () => {
  const context = useFeatureFlagsOptional();
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
